import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isSyncing, setIsSyncing] = useState(true);
  const [stockError, setStockError] = useState(null);

  const localGetCart = () => JSON.parse(localStorage.getItem("cart")) || [];
  const localSaveCart = (cart) => localStorage.setItem("cart", JSON.stringify(cart));
  const localClearCart = () => localStorage.removeItem("cart");

  const apiFetchCart = async () => {
  if (!user || !user.id) {
    console.warn("apiFetchCart appelé sans utilisateur");
    return;
  }

  try {
    const res = await fetch(`/api/cart.php?userId=${user.id}`, {
      credentials: "include",
    });
    if (res.ok) {
      const data = await res.json();
      setCartItems(data);
    } else {
      const err = await res.text();
      console.error("Erreur fetchCart:", err);
      setCartItems([]);
    }
  } catch (err) {
    console.error("Erreur apiFetchCart:", err);
    setCartItems([]);
  }
};

  const apiSyncLocalToBackend = async () => {
    const localCart = localGetCart();
    if (localCart.length === 0) return;

    try {
      const res = await fetch(`/api/cart.php?userId=${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          items: localCart.map(p => ({
            product_id: p.product_id || p.id,
            variante_id: p.variante_id,
            variante_option_id: p.variante_option_id,
            quantity: p.quantity
          }))
        }),
      });
      if (res.ok) {
        await apiFetchCart();
        localClearCart();
      } else {
        const err = await res.text();
        console.error("Erreur sync:", err);
      }
    } catch (err) {
      console.error("Erreur apiSyncLocalToBackend:", err);
    }
  };

  // ==== Synchronisation au chargement ====
  useEffect(() => {
    const syncAndFetch = async () => {
      const rawLocalCart = localGetCart();

      const migratedCart = rawLocalCart.map(item => ({
        ...item,
        variante_option_id:
        item.variante_option_id ??
        item.selectedOption?.id ??
        null
      }));

    // Sauvegarde le panier corrigé dans le localStorage
    localSaveCart(migratedCart);
      if (user?.id) {
        setIsSyncing(true);
        const localCart = localGetCart();
        if (localCart.length > 0) {
          await apiSyncLocalToBackend();
        }
        await apiFetchCart();
        localClearCart();
        setIsSyncing(false);
      } else {
        const localCart = localGetCart();
        setCartItems(localCart);
        setIsSyncing(false);
      }
    };
    syncAndFetch();
  }, [user?.id]);

  // ==== Fonctions locales ====

  const localAdd = (product) => {
  const cart = localGetCart();
  const index = cart.findIndex(
    (item) => item.variante_option_id === product.variante_option_id
  );

  if (index !== -1) {
    cart[index].quantity += product.quantity;
  } else {
    cart.push({
      ...product,
      gout: product.gout,
      poids: product.poids,
      variante_option_id: product.variante_option_id ?? product.selectedOption?.id ?? null,
      variante_id: product.variante_id,
      product_id: product.product_id || product.id,
    });
  }
  localSaveCart(cart);
  setCartItems(cart);
};

  const localRemove = (variante_option_id) => {
    const newCart = localGetCart().filter(
      (item) => item.variante_option_id !== variante_option_id
    );
    localSaveCart(newCart);
    setCartItems(newCart);
  };

const localUpdate = (variante_option_id, quantity) => {
  const cart = localGetCart().map((item) =>
    item.variante_option_id === variante_option_id
      ? { ...item, quantity }
      : item
  );
  localSaveCart(cart);
  setCartItems(cart);
};

  const localClear = () => {
    localClearCart();
    setCartItems([]);
  };

  // ==== Backend ====

  const apiAdd = async (product) => {
    const productToSend = {
      ...product,
      variante_option_id: product.variante_option_id,
      gout: product.gout,
      poids: product.poids,
      variante_id: product.variante_id,
    };

    const res = await fetch(`/api/cart.php?userId=${user.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(productToSend),
    });

    if (!res.ok) {
      console.error("Erreur add:", await res.text());
    }
  };

  const apiRemove = async (variante_option_id) => {
    const res = await fetch(`/api/cart.php`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ variante_option_id }),
    });

    if (!res.ok) {
      console.error("Erreur delete:", await res.text());
    }
  };

  const apiUpdate = async (variante_option_id, quantity) => {
    const res = await fetch(`/api/cart.php`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ variante_option_id, quantity }),
    });

    if (!res.ok) {
      console.error("Erreur update:", await res.text());
    }
  };

  const apiClear = async () => {
    await fetch(`/api/cart.php?clear=true&userId=${user.id}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    setCartItems([]);
  };

  // ==== Méthodes exposées ====
  const checkStock = async (variante_option_id, quantity) => {
  try {
    const res = await fetch("/api/check_stock.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ produits: [{ variante_option_id, quantite: quantity }] }),
    });

    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (err) {
      console.error("Erreur parse JSON:", err, text);
      return { ok: false, message: "Impossible de vérifier le stock pour le moment (JSON invalide)." };
    }

    return data; 
  } catch (err) {
    console.error("Erreur vérification stock:", err);
    return { ok: false, message: "Impossible de vérifier le stock pour le moment." };
  }
};

  const addToCart = async (product) => {
  if (isSyncing) return;

  const result = await checkStock(product.variante_option_id, product.quantity);
  if (!result.ok) {
    setStockError(result.message);
    return false;
  }

  if (user?.id) {
    await apiAdd(product);
    await apiFetchCart();
  } else {
    localAdd(product);
  }
  return true;
};

  const removeFromCart = async (variante_option_id) => {
    if (user?.id) {
      await apiRemove(variante_option_id); 
      await apiFetchCart();
    } else {
      localRemove(variante_option_id);
    }
  };

  const updateQuantity = async (variante_option_id, quantity) => {
  if (quantity < 1) quantity = 1;

  try {
    const res = await fetch("/api/check_stock.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        produits: [{ variante_option_id, quantite: quantity }]
      }),
    });

    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (err) {
      console.error("Erreur parse JSON:", err, text);
      alert("Impossible de vérifier le stock pour le moment (JSON invalide).");
      return;
    }

    if (!data.ok) {
      setStockError(data.message);
      return;
    }

  } catch (err) {
    console.error("Erreur vérification stock:", err);
    alert("Impossible de vérifier le stock pour le moment.");
    return;
  }

  if (user?.id) {
    await apiUpdate(variante_option_id, quantity);
    await apiFetchCart();
  } else {
    localUpdate(variante_option_id, quantity);
  }

  setCartItems((prevItems) =>
    prevItems.map((item) =>
      item.variante_option_id === variante_option_id
        ? { ...item, quantity }
        : item
    )
  );
};
  const clearCart = async () => {
    if (user?.id) {
      await apiClear();
      await apiFetchCart();
    } else {
      localClear();
    }
  };

  const total = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + (item.prix_final ?? item.price) * item.quantity, 0)
    : 0;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        total,
        apiFetchCart,
        apiSyncLocalToBackend, 
        stockError,
        setStockError
      }}
    >
      {children}
    </CartContext.Provider>
  );
}